package com.siriusxm.pia

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import com.siriusxm.unifiedcontent.auth.ActionIdentifier
import com.siriusxm.unifiedcontent.auth.BatchAuthorizationWithTokenRequest
import com.siriusxm.unifiedcontent.auth.BatchIsAuthorizedWithTokenRequest
import com.siriusxm.unifiedcontent.auth.Decision
import com.siriusxm.unifiedcontent.auth.EntityIdentifier
import com.siriusxm.unifiedcontent.auth.UnifiedAuthorizer
import com.siriusxm.unifiedcontent.auth.UnifiedAuthorizerConfig
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.delay
import kotlinx.serialization.json.JsonObject

/**
 * A named unified-content (AVP / Cedar) action (UCA-1091).
 *
 * Applications define their own actions as ordinary constants next to the code that gates on them —
 * cmc-core deliberately does not enumerate them — so any application can introduce a capability
 * without editing shared code. The [actionId] must match the Cedar action id the *server* enforces
 * for the same operation; that pairing is the contract, and the client can only ever use it to *hide*
 * an affordance, never to grant access.
 *
 * Every console action shares the `SXM::Action` Cedar type, so callers normally name just the id:
 *
 *     val ViewChatTranscripts = CapabilityAction("UnifiedContentManagementConsole_ViewChatTranscripts")
 *
 * @property actionId the Cedar action id (the part after `SXM::Action::`)
 * @property actionType the Cedar action *type*; the platform default `SXM::Action` is almost always right
 */
public data class CapabilityAction(
    public val actionId: String,
    public val actionType: String = DEFAULT_ACTION_TYPE,
) {
    public companion object {
        /** The Cedar action *type* shared by every console action; AVP takes type and id separately. */
        public const val DEFAULT_ACTION_TYPE: String = "SXM::Action"
    }
}

/**
 * A Cedar resource an action targets, e.g. `("SXM::Entity", "chan-123")` (UCA-1091).
 *
 * Supplied only for resource-scoped decisions ("can I edit *this* entity"); most affordance checks are
 * action-only and leave the resource null.
 *
 * @property type the Cedar entity type
 * @property id the Cedar entity id
 */
public data class CapabilityResource(
    public val type: String,
    public val id: String,
)

/**
 * A single authorization question: an [action], optionally against a [resource], optionally with a
 * Cedar [context] (UCA-1091).
 *
 * The query — not the action alone — is the unit that is evaluated and cached, because once a decision
 * depends on a resource or context the action no longer identifies the answer (an "edit" action is
 * allowed for one entity and denied for another). Two queries that differ in any field are two cache
 * entries. Equality is structural (data class), including [context], which has value equality in
 * kotlinx-serialization.
 *
 * The action-only case is just a query with both optionals null, so gating a simple affordance stays a
 * one-liner via [CapabilityAction.invoke] or [ApplicationContext.can].
 */
public data class CapabilityQuery(
    public val action: CapabilityAction,
    public val resource: CapabilityResource? = null,
    public val context: JsonObject? = null,
)

/**
 * Build a [CapabilityQuery] from an action, optionally scoped to a [resource] and/or [context].
 *
 * Lets call sites read as `EditEntity(resource = CapabilityResource("SXM::Entity", id))` rather than
 * spelling out `CapabilityQuery(EditEntity, ...)`.
 */
public operator fun CapabilityAction.invoke(
    resource: CapabilityResource? = null,
    context: JsonObject? = null,
): CapabilityQuery = CapabilityQuery(this, resource, context)

/**
 * Discovers, from the browser, which unified-content (AVP) authorization questions the current user's
 * token is allowed — the reusable client-side counterpart to the server's `requireAvpAction`
 * (UCA-1091).
 *
 * ### What this is and is not
 * This is a **UI affordance helper only**. The authorization boundary is the server: every gated API
 * operation independently validates the caller's token against AVP and returns 403. A capability here
 * may only decide whether to *show* a link or menu item; it must never be treated as the thing that
 * *grants* access. Accordingly it **fails closed** — if there is no auth-check URL, or the check
 * throws, or a query was never resolved, [isAllowed] returns `false` so the affordance stays hidden.
 *
 * ### How it works
 * The browser calls the shared unified-content `POST /auth-check` API through the `unified-authorizer`
 * JS client, passing the user's access token as the bearer (the same token the console already sends
 * to CMC APIs, which AVP validates against its Cognito identity source). One
 * `batchIsAuthorizedWithToken` call resolves up to 30 queries at once and the per-query ALLOW/DENY
 * decisions are cached for the life of the page load (the token is stable for a load). Permissions are
 * granted identically across environments, so a single (prod) auth-check URL serves every stage.
 *
 * The cache collapses duplicates: [refresh] only sends queries it has not already resolved, so asking
 * for actions one at a time and asking for a batch reach the same cache — the only difference is how
 * many network round trips, and repeats are free.
 */
public interface Capabilities {
    /**
     * Ensure [queries] have been evaluated (issuing one batch AVP call for any not yet cached) and
     * return whether each of them is allowed. Safe to call repeatedly; already-resolved queries are not
     * re-fetched, and a query another call is still checking is awaited rather than sent again or read
     * as denied. On any failure the unresolved queries resolve to denied and the error is logged.
     */
    public suspend fun refresh(queries: Collection<CapabilityQuery>): Map<CapabilityQuery, Boolean>

    /** Resolve a single [query] (issuing the AVP check on first use) and return whether it is allowed. */
    public suspend fun isAuthorized(query: CapabilityQuery): Boolean

    /**
     * Whether [query] is currently known to be allowed. Returns `false` when the query has not been
     * resolved yet or resolved to denied — callers that need a definitive answer should [refresh]
     * first (or use [isAuthorized] / [ApplicationContext.can]).
     */
    public fun isAllowed(query: CapabilityQuery): Boolean

    /**
     * Whether [query] has a cached answer, allowed or denied. False while it has not been asked, while
     * it is still being checked, and after a check that failed, since a failed check is not cached
     * (UCA-1281). [resolveCapabilities] uses this to tell a real denial from one it should retry.
     * Defaults to true, which never retries.
     */
    public fun isResolved(query: CapabilityQuery): Boolean = true
}

/**
 * Delays between attempts when a capability check fails, in milliseconds (UCA-1281). After the last
 * one, a query that still could not be checked stays denied until something asks for it again.
 */
private val CAPABILITY_RETRY_DELAYS_MS: List<Long> = listOf(2_000, 5_000, 15_000, 30_000)

/**
 * Resolve [queries], retrying any that could not be checked, and pass each set of answers to
 * [onAnswer] (UCA-1281). The first answer arrives after the first attempt, with failed checks reading
 * as denied. If any failed, they are asked again after each of [CAPABILITY_RETRY_DELAYS_MS], and
 * [onAnswer] gets the new answers each time. Stops once every query has a cached answer.
 *
 * Use this from a Compose effect, which cancels the retries when it leaves composition. Without it, a
 * single failed `/auth-check` call would hide an affordance for as long as the screen stays mounted,
 * because the effect's key never changes. Fails closed, like [Capabilities.refresh].
 */
public suspend fun Capabilities.resolveCapabilities(
    queries: Collection<CapabilityQuery>,
    onAnswer: (Map<CapabilityQuery, Boolean>) -> Unit
) {
    onAnswer(refresh(queries))
    for (wait in CAPABILITY_RETRY_DELAYS_MS) {
        val unresolved = queries.filterNot { isResolved(it) }
        if (unresolved.isEmpty()) return
        delay(wait)
        refresh(unresolved)
        onAnswer(queries.associateWith { isAllowed(it) })
    }
}

/**
 * The default [Capabilities], backed by the `unified-authorizer` browser client.
 *
 * @param authCheckApiUrl base URL of the shared `/auth-check` API, or null to disable checks entirely
 *        (every query then resolves to denied — the fail-closed default when unconfigured).
 * @param tokenProvider supplies the current bearer (access) token at call time; the same provider the
 *        rest of the console uses, so token refreshes are picked up automatically.
 */
public class AvpCapabilities(
    private val authCheckApiUrl: String?,
    private val tokenProvider: () -> String?
) : Capabilities {

    /** Lazily built only when a URL is configured; null keeps the whole helper fail-closed. */
    private val authorizer: UnifiedAuthorizer? by lazy {
        val url = authCheckApiUrl?.takeIf { it.isNotBlank() } ?: return@lazy null
        UnifiedAuthorizer(
            UnifiedAuthorizerConfig(
                tokenProvider = { tokenProvider() ?: "" },
                apiUrl = url
            )
        )
    }

    /**
     * Answered per-query decisions accumulated across [refresh] calls for this page load. Holds only
     * answers AVP gave, never a placeholder for a check that is still running.
     */
    private val decisions: MutableMap<CapabilityQuery, Boolean> = mutableMapOf()

    /**
     * Queries one [refresh] has sent and AVP has not answered yet (UCA-1281). Another [refresh] that
     * asks one of these awaits it instead of sending it again or reading it as decided. Completes with
     * the decision, with `false` when the check failed, or with null when the call that sent it was
     * cancelled, in which case the waiter asks again.
     */
    private val inFlight: MutableMap<CapabilityQuery, CompletableDeferred<Boolean?>> = mutableMapOf()

    override suspend fun refresh(queries: Collection<CapabilityQuery>): Map<CapabilityQuery, Boolean> {
        val client = authorizer
        val toSend = queries.filter { it !in decisions && it !in inFlight }.distinct()
        if (client == null) {
            // No auth-check configured: everything the caller asked about is denied (fail closed).
            toSend.forEach { decisions[it] = false }
            return queries.associateWith { decisions[it] == true }
        }

        // Checks another call already has running, captured before this call registers its own.
        val awaiting = queries.distinct().mapNotNull { query -> inFlight[query]?.let { query to it } }
        // Answers for this call only that must not be cached: a failed check reads as denied here,
        // but is asked again by the next refresh.
        val uncached = mutableMapOf<CapabilityQuery, Boolean>()

        // Register every query this call sends before the first request, so a concurrent refresh
        // waits for it rather than sending a duplicate.
        val sent = toSend.associateWith { CompletableDeferred<Boolean?>() }
        inFlight.putAll(sent)
        try {
            // The batch API resolves at most MAX_BATCH_SIZE questions per call, and exceeding it fails
            // the whole request, so a caller asking about more affordances than that must be split.
            // Send the queries in chunks and resolve each chunk independently: one chunk's network
            // failure only affects that chunk, not the rest.
            toSend.chunked(MAX_BATCH_SIZE).forEach { chunk ->
                // Retain each full query beside the serialized request. AVP returns batch results in
                // request order, so pairing by position preserves resource/context data even when the
                // HTTP wrapper's echoed `request` omits part of the submitted request.
                try {
                    // Built inside the try so a query that cannot be serialized fails closed with the
                    // rest of its chunk rather than escaping refresh.
                    val submitted = chunk.map { query -> query to query.toRequest() }
                    val response = client.batchIsAuthorizedWithToken(
                        BatchIsAuthorizedWithTokenRequest(requests = submitted.map { it.second })
                    )
                    response.results.zip(submitted).forEach { (result, submission) ->
                        decisions[submission.first] = result.decision == Decision.ALLOW
                    }
                    // A query that never comes back in the response stays closed.
                    chunk.forEach { query ->
                        if (query !in decisions) decisions[query] = false
                        inFlight.remove(query)
                        sent.getValue(query).complete(decisions[query])
                    }
                } catch (e: CancellationException) {
                    // Compose cancels the effect when its query leaves composition. Cancellation is
                    // control flow, not an authorization failure: the finally below leaves the queries
                    // unanswered so a waiter or a future caller asks again.
                    throw e
                } catch (e: Throwable) {
                    // Fail closed on any error (network, CORS, token rejected): the answer this call
                    // and its waiters get for the chunk's queries is denied. But do NOT cache that
                    // denial, so a *transient* failure is retried on the next refresh rather than
                    // hiding the affordance for the rest of the page load. isAllowed still reads false
                    // for an unanswered query, so the fail-closed guarantee holds meanwhile.
                    console.error("AVP capability check failed; treating queries as denied (will retry)", e)
                    chunk.forEach { query ->
                        uncached[query] = false
                        inFlight.remove(query)
                        sent.getValue(query).complete(false)
                    }
                }
            }
        } finally {
            // On cancellation, release every query this call registered and has not answered, so
            // waiters are not left suspended and the queries can be sent again.
            sent.forEach { (query, deferred) ->
                if (inFlight[query] === deferred) inFlight.remove(query)
                deferred.complete(null)
            }
        }

        // Wait for the checks other calls were running. A null means that call was cancelled before
        // AVP answered, so ask again: either this call sends it, or it waits on whoever sent it since.
        val askAgain = awaiting.mapNotNull { (query, deferred) ->
            when (val answer = deferred.await()) {
                null -> query
                else -> {
                    if (query !in decisions) uncached[query] = answer
                    null
                }
            }
        }
        if (askAgain.isNotEmpty()) uncached.putAll(refresh(askAgain))

        return queries.associateWith { decisions[it] ?: uncached[it] ?: false }
    }

    override suspend fun isAuthorized(query: CapabilityQuery): Boolean =
        refresh(setOf(query))[query] == true

    override fun isAllowed(query: CapabilityQuery): Boolean = decisions[query] == true

    override fun isResolved(query: CapabilityQuery): Boolean = query in decisions

    /**
     * Translate a [CapabilityQuery] into the shim's request shape.
     */
    private fun CapabilityQuery.toRequest(): BatchAuthorizationWithTokenRequest =
        BatchAuthorizationWithTokenRequest(
            action = ActionIdentifier(actionType = action.actionType, actionId = action.actionId),
            resource = resource?.let { EntityIdentifier(entityType = it.type, entityId = it.id) },
            context = context
        )

    private companion object {
        /**
         * Maximum authorization questions the batch API resolves in one call; a request exceeding it is
         * rejected wholesale, so [refresh] chunks at this size.
         */
        private const val MAX_BATCH_SIZE = 30
    }
}

/**
 * Resolve [action] (action-only, no resource or context) for the current user and return whether it is
 * allowed. The common "check one affordance" call site; safe to call from a `LaunchedEffect`. Fails
 * closed like the rest of [Capabilities].
 */
public suspend fun ApplicationContext.can(action: CapabilityAction): Boolean =
    can(CapabilityQuery(action))

/**
 * Resolve [query] for the current user and return whether it is allowed. Use the [CapabilityQuery]
 * form when the decision is scoped to a resource or needs a Cedar context; otherwise the
 * [CapabilityAction] overload is terser.
 */
public suspend fun ApplicationContext.can(query: CapabilityQuery): Boolean =
    capabilities.isAuthorized(query)

/**
 * Reactively resolve [action] for the current user, returning a [State] that reads `false` on first
 * composition and flips to the real answer once AVP responds (recomposing the caller). The AVP check
 * runs in a `LaunchedEffect` keyed on the action, so the affordance simply appears once authorized and
 * never flashes for users who are not.
 */
@Composable
public fun ApplicationContext.rememberCapability(action: CapabilityAction): State<Boolean> =
    rememberCapability(CapabilityQuery(action))

/**
 * Reactively resolve [query] for the current user, returning a [State] that reads `false` until AVP
 * answers (see the [CapabilityAction] overload). Keyed on the whole query, so changing the resource or
 * context re-checks.
 */
@Composable
public fun ApplicationContext.rememberCapability(query: CapabilityQuery): State<Boolean> {
    val allowed = remember(query) { mutableStateOf(false) }
    LaunchedEffect(query) {
        // Retries a failed check while this stays in composition (UCA-1281).
        capabilities.resolveCapabilities(setOf(query)) { answers ->
            allowed.value = answers[query] == true
        }
    }
    return allowed
}
